import dayjs from 'dayjs'
import type { ComputedRef, Ref } from 'vue'
import type { EventDate } from '~/composables/types/event'
import { getFirstBookableDate } from '~/utils/dateHelpers'

export interface DateRequestDateSlots {
  datePickerStartTime: Ref<{ hours: number; minutes: number }>
  minDateDayjs: ComputedRef<dayjs.Dayjs>
  minDateTimeLocal: ComputedRef<string>
  minDatePickerStart: ComputedRef<Date>
  getStandardDisabledWeekDays: () => number[]
  isStandardCalendarDateDisabled: (date: Date) => boolean
  isStandardSlotDateAvailable: (
    userDate: string,
    availableDates: readonly EventDate[]
  ) => boolean
}

/**
 * Provides date-picker helpers (min date, disabled days, native input
 * normalization, slot availability). Pure helpers that read from
 * `useEventData()`; no form state is owned here.
 */
export const useDateRequestDateSlots = (): DateRequestDateSlots => {
  const eventData = useEventData()

  const datePickerStartTime = ref({ hours: 18, minutes: 0 })

  const minDateDayjs = computed(() =>
    dayjs().add(
      eventGetters.getDateRequestMinAdvanceQuantity(eventData.value),
      eventGetters
        .getDateRequestMinAdvanceUnit(eventData.value)
        .toLowerCase() as dayjs.ManipulateType
    )
  )
  // Full datetime-local floor for the native mobile input (a date-only `min` is
  // ignored by `datetime-local`, so the min-advance lead time was not enforced).
  const minDateTimeLocal = computed(() =>
    minDateDayjs.value.format('YYYY-MM-DDTHH:mm')
  )
  // First selectable slot: min advance advanced to the first available weekday.
  // Used for the desktop picker's start/min date so it opens on a bookable day
  // instead of today. Anything earlier is either before min advance or on a
  // disabled weekday, so this is also the true minimum.
  const minDatePickerStart = computed(() =>
    getFirstBookableDate(
      minDateDayjs.value,
      eventGetters.getDateRequestAvailableDays(eventData.value)
    ).toDate()
  )

  const getStandardDisabledWeekDays = () =>
    [0, 1, 2, 3, 4, 5, 6].filter(
      (day) =>
        !eventGetters.getDateRequestAvailableDays(eventData.value).includes(day)
    )

  const isStandardCalendarDateDisabled = (date: Date) =>
    dayjs(date).isBefore(minDateDayjs.value.startOf('day'))

  const isStandardSlotDateAvailable = (
    userDate: string,
    availableDates: readonly EventDate[]
  ) => {
    if (!userDate || !availableDates || availableDates.length === 0) {
      return false
    }
    return availableDates.some((availableDate) => {
      const userDateObj = dayjs(userDate)
      const availableDateObj = dayjs(availableDate.start)
      if (!userDateObj.isSame(availableDateObj, 'day')) {
        return false
      }
      const timeDiffMinutes = Math.abs(
        userDateObj.diff(availableDateObj, 'minutes')
      )
      return timeDiffMinutes <= 60
    })
  }

  return {
    datePickerStartTime,
    minDateDayjs,
    minDateTimeLocal,
    minDatePickerStart,
    getStandardDisabledWeekDays,
    isStandardCalendarDateDisabled,
    isStandardSlotDateAvailable,
  }
}
